import { TReactSetState, TUseTranslationTfn } from "../../data/AppType";
import {
  handleError,
  prepareMessageFromParams,
  sweetAlertError,
  sweetAlertSuccess,
  toastError,
} from "../../utils/AppFunctions";
import * as Yup from "yup";
import {
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import {
  CREATE_MEASUREMENT_TYPE,
  GET_MEASUREMENT_TYPE_BY_ID,
  MEASUREMENT_FIELD_DDL,
  UPDATE_MEASUREMENT_TYPE,
} from "../../services/Endpoints";
import { IOption } from "../../data/AppInterface";
import { BIT_VALUE } from "../../utils/AppEnumerations";
import { IMeasurementType } from "./MeasurementTypeListController";

export interface IMeasurementFieldItem {
  measurement_unit: string;
  measurement_unit_label: string;
}
export interface IConfiguration {
  measurement_unit: string;
  measurement_unit_label: string;
  measurement_field_id: string | null;
  sort_order: number | null;
}

export interface IMeasurementTypeForm extends Omit<IMeasurementType, "id"> {
  id?: string;
  image_url: string;
  configuration: IConfiguration[];
}

export const MEASUREMENT_TYPE_INITIAL_VALUES = {
  type_name: "",
  turn_around_time: null,
  job_completion_time: null,
  gender: "",
  image_url: "",
  is_active: BIT_VALUE.One,
  configuration: [
    {
      measurement_field_id: null,
      measurement_unit: "",
      measurement_unit_label: "",
      sort_order: null,
    },
  ],
};

export const measurementTypeValidationSchema = (t: TUseTranslationTfn) =>
  Yup.object().shape({
    type_name: Yup.string()
      .required(
        prepareMessageFromParams(t("error_message_required"), [
          ["fieldName", t("type_name")],
        ]),
      )
      .max(
        50,
        prepareMessageFromParams(t("error_message_max_length"), [
          ["fieldName", t("type_name")],
          ["max", "50"],
        ]),
      )
      .min(
        2,
        prepareMessageFromParams(t("error_message_min_length"), [
          ["fieldName", t("type_name")],
          ["min", "2"],
        ]),
      ),

    turn_around_time: Yup.number()
      .required(
        prepareMessageFromParams(t("error_message_required"), [
          ["fieldName", t("turn_around_time")],
        ]),
      )
      .min(
        1,
        prepareMessageFromParams(t("error_message_greater_than"), [
          ["fieldName", t("turn_around_time")],
          ["min", "0"],
        ]),
      )
      .max(
        99,
        prepareMessageFromParams(t("error_message_max_length"), [
          ["fieldName", t("turn_around_time")],
          ["max", "99"],
        ]),
      ),
    job_completion_time: Yup.number()
      .required(
        prepareMessageFromParams(t("error_message_required"), [
          ["fieldName", t("job_completion_time")],
        ]),
      )
      .max(
        999,
        prepareMessageFromParams(t("error_message_max_length"), [
          ["fieldName", t("job_completion_time")],
          ["max", "999"],
        ]),
      )
      .min(
        1,
        prepareMessageFromParams(t("error_message_greater_than"), [
          ["fieldName", t("job_completion_time")],
          ["min", "0"],
        ]),
      ),
    gender: Yup.string().required(
      prepareMessageFromParams(t("error_message_required"), [
        ["fieldName", t("gender")],
      ]),
    ),
    configuration: Yup.array().of(
      Yup.object().shape({
        measurement_field_id: Yup.string().required(
          prepareMessageFromParams(t("error_message_required"), [
            ["fieldName", t("measurement_field")],
          ]),
        ),
        sort_order: Yup.number()
          .required(t("required"))
          .min(1, "min 1")
          .max(100, "max 100"),
      }),
    ),
  });

export const fetchMeasurementTypeById = async (
  id: string,
  handleClose: (status: boolean) => void,
) => {
  try {
    const measurementTypeData = await GET_MEASUREMENT_TYPE_BY_ID(id);
    if (
      measurementTypeData &&
      measurementTypeData.code === DEFAULT_STATUS_CODE_SUCCESS
    ) {
      return measurementTypeData.data;
    } else {
      handleClose(false);
      sweetAlertError({
        message: measurementTypeData.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
      return undefined;
    }
  } catch (e: any) {
    handleClose(false);
    handleError(e, "fetchMeasurementTypeById");
  }
};

export const handleSubmitMeasurementType = async (
  values: IMeasurementTypeForm,
  setLoading: TReactSetState<boolean>,
  id: string | undefined,
  handleClose: (status: boolean) => void,
) => {
  setLoading(true);
  try {
    let resultMeasurementType;
    if (id) {
      resultMeasurementType = await UPDATE_MEASUREMENT_TYPE(values, id);
    } else {
      resultMeasurementType = await CREATE_MEASUREMENT_TYPE(values);
    }
    if (
      resultMeasurementType &&
      resultMeasurementType.code === DEFAULT_STATUS_CODE_SUCCESS
    ) {
      sweetAlertSuccess({
        message: resultMeasurementType.message,
      });
      return handleClose(true);
    } else {
      sweetAlertError({
        message:
          resultMeasurementType?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (e: any) {
    handleError(e, "handleSubmitMeasurementType");
  } finally {
    setLoading(false);
  }
};

export const getMeasurementFieldDdl = async (
  setMeasurementField: TReactSetState<IOption<string, IMeasurementFieldItem>[]>,
  required_ids: string[],
) => {
  try {
    const measurementFieldDDLList = await MEASUREMENT_FIELD_DDL({
      required_ids,
    });
    if (
      measurementFieldDDLList &&
      measurementFieldDDLList.code === DEFAULT_STATUS_CODE_SUCCESS
    ) {
      setMeasurementField(measurementFieldDDLList.data || []);
    } else {
      toastError(
        measurementFieldDDLList.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      );
    }
  } catch (e: any) {
    toastError(e?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
    handleError(e, "getMeasurementFieldDdl");
  }
};
